import { useId, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import clsx from 'clsx'
import { Icon } from './Icon'
import './ui.css'

interface FieldShellProps {
  label?: string
  optional?: boolean
  hint?: string
  error?: string | null
  counter?: string
  children: (id: string, invalid: boolean) => ReactNode
  className?: string
}

export function Field({ label, optional, hint, error, counter, children, className }: FieldShellProps) {
  const id = useId()
  const invalid = Boolean(error)
  return (
    <div className={clsx('field', invalid && 'field--invalid', className)}>
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
          {optional && <span className="field__optional"> · opcional</span>}
        </label>
      )}
      {children(id, invalid)}
      {counter && <span className="field__counter">{counter}</span>}
      {error ? (
        <span className="field__error" role="alert">
          <Icon name="warning" size={14} strokeWidth={1.8} />
          {error}
        </span>
      ) : (
        hint && <span className="field__hint">{hint}</span>
      )}
    </div>
  )
}

type TextFieldProps = {
  label?: string
  optional?: boolean
  hint?: string
  error?: string | null
  counter?: string
  display?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>

export function TextField({ label, optional, hint, error, counter, display, ...rest }: TextFieldProps) {
  return (
    <Field label={label} optional={optional} hint={hint} error={error} counter={counter}>
      {(id, invalid) => (
        <input
          id={id}
          className={clsx('field__control', display && 'field__control--name')}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </Field>
  )
}

type PasswordFieldProps = Omit<TextFieldProps, 'type' | 'display'>

/** A password input that can be peeked at — the eye never leaves the field. */
export function PasswordField({ label, optional, hint, error, counter, ...rest }: PasswordFieldProps) {
  const [shown, setShown] = useState(false)
  return (
    <Field label={label} optional={optional} hint={hint} error={error} counter={counter}>
      {(id, invalid) => (
        <div className="field__pass">
          <input
            id={id}
            type={shown ? 'text' : 'password'}
            className="field__control"
            aria-invalid={invalid || undefined}
            {...rest}
          />
          <button
            type="button"
            className="field__reveal"
            onClick={() => setShown((v) => !v)}
            aria-label={shown ? 'Ocultar la contraseña' : 'Ver la contraseña'}
            aria-pressed={shown}
            tabIndex={-1}
          >
            <Icon name={shown ? 'eye-off' : 'eye'} size={20} strokeWidth={1.8} />
          </button>
        </div>
      )}
    </Field>
  )
}

type TextAreaProps = {
  label?: string
  optional?: boolean
  hint?: string
  error?: string | null
  counter?: string
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>

export function TextArea({ label, optional, hint, error, counter, ...rest }: TextAreaProps) {
  return (
    <Field label={label} optional={optional} hint={hint} error={error} counter={counter}>
      {(id, invalid) => (
        <textarea id={id} className="field__control" aria-invalid={invalid || undefined} {...rest} />
      )}
    </Field>
  )
}
